<template>
  <div>
    <div class="sw-opciones sw-opciones--2" role="radiogroup" :aria-label="t('¿Para quién es el encargo?')">
      <button type="button" class="sw-opcion" role="radio"
        :aria-checked="estado.presentador.tipo === 'INTERESADO'"
        :class="{ activo: estado.presentador.tipo === 'INTERESADO' }"
        @click="elegir('INTERESADO')">
        <v-icon class="sw-opcion__icono">mdi-account-outline</v-icon>
        <span class="sw-opcion__titulo">{{ t('Para mí') }}</span>
        <span class="sw-opcion__texto">{{ t('Voy a firmar yo, solo o con otras personas.') }}</span>
      </button>
      <button type="button" class="sw-opcion" role="radio"
        :aria-checked="estado.presentador.tipo === 'PROFESIONAL'"
        :class="{ activo: estado.presentador.tipo === 'PROFESIONAL' }"
        @click="elegir('PROFESIONAL')">
        <v-icon class="sw-opcion__icono">mdi-briefcase-outline</v-icon>
        <span class="sw-opcion__titulo">{{ t('Para un cliente') }}</span>
        <span class="sw-opcion__texto">{{ t('Soy abogado, gestor, asesor o agente inmobiliario y la preparo en su nombre.') }}</span>
      </button>
    </div>
    <p v-if="errores['presentador.tipo']" class="sw-error">{{ errores['presentador.tipo'] }}</p>

    <div v-if="estado.presentador.tipo === 'PROFESIONAL'" ref="bloqueProfesional" class="sw-bloque">
      <p class="sw-subtitulo">{{ t('Sus datos de contacto') }}</p>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field v-model="pro.nombre" :label="t('Nombre y apellidos')" autocomplete="name"
            variant="outlined" density="comfortable" :error-messages="m('profesional.nombre')"
            @update:model-value="limpiarError('profesional.nombre')" />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="pro.empresa" :label="t('Despacho o empresa (opcional)')" autocomplete="organization"
            variant="outlined" density="comfortable" />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="pro.telefono" :label="t('Teléfono')" type="tel" inputmode="tel" autocomplete="tel"
            variant="outlined" density="comfortable" :hint="t('Si no es español, con prefijo: +44…')"
            :error-messages="m('profesional.telefono')"
            @update:model-value="limpiarError('profesional.telefono')" @blur="tocados.telefono = true">
            <template #append-inner><v-icon v-if="tocados.telefono && pro.telefono && !errorTelefono(pro.telefono)" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="pro.email" :label="t('Email')" type="email" inputmode="email" autocomplete="email"
            spellcheck="false" variant="outlined" density="comfortable" :error-messages="m('profesional.email')"
            :messages="sugerencia ? [sugerencia] : []" :class="{ 'sw-sugerencia': sugerencia }"
            @update:model-value="limpiarError('profesional.email')" @blur="pro.email = pro.email.trim(); tocados.email = true">
            <template #append-inner><v-icon v-if="tocados.email && pro.email && !errorEmail(pro.email)" color="success" size="20">mdi-check-circle</v-icon></template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { t } from '../../composables/solicitudTextos'
import { computed, nextTick, reactive, ref } from 'vue'
import type { TipoPresentador } from '../../types/solicitudWeb'
import { errorEmail, errorTelefono, sugerenciaEmail, useSolicitudWeb } from '../../composables/useSolicitudWeb'
const { estado, errores, limpiarError, siguiente, pasoActual } = useSolicitudWeb()
let temporizador: ReturnType<typeof setTimeout> | null = null
const bloqueProfesional = ref<HTMLElement | null>(null)

/**
 * "La relleno yo" no necesita más datos: pasa al siguiente paso tras una pausa breve para que se vea la selección.
 * "Por un cliente" se queda para rellenar los datos del profesional.
 */
async function elegir(tipo: TipoPresentador) {
  estado.value.presentador.tipo = tipo
  limpiarError('presentador.tipo')
  if (temporizador) clearTimeout(temporizador)
  if (tipo === 'INTERESADO') {
    // Solo avanza si sigue en este paso (evita saltos dobles con doble clic)
    temporizador = setTimeout(() => { if (pasoActual.value === 'presentador') siguiente() }, 280)
  } else {
    await nextTick()
    bloqueProfesional.value?.querySelector('input')?.focus()
  }
}
const pro = computed(() => estado.value.presentador.profesional)
const tocados = reactive({ telefono: false, email: false })

/** Error enviado por "Continuar" o, si el campo ya se ha tocado, comprobación en vivo. */
function m(k: string): string[] {
  if (errores.value[k]) return [errores.value[k]]
  if (k === 'profesional.telefono' && tocados.telefono) {
    const e = errorTelefono(pro.value.telefono)
    if (e) return [e]
  }
  if (k === 'profesional.email' && tocados.email) {
    const e = errorEmail(pro.value.email)
    if (e) return [e]
  }
  return []
}

const sugerencia = computed(() => {
  if (!tocados.email) return null
  const dominio = sugerenciaEmail(pro.value.email)
  return dominio ? t('¿Quería decir @{dominio}?', { dominio }) : null
})
</script>
